import React from 'react';
import { Loader2 } from 'lucide-react';

const Button = ({
  children,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'left',
  fullWidth = false,
  className = '',
  onClick = () => {},
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500',
    secondary: 'bg-secondary-600 text-white hover:bg-secondary-700 focus:ring-secondary-500',
    outline: 'bg-transparent border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-primary-500',
    ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-primary-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    warning: 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500',
    info: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    link: 'bg-transparent text-primary-600 hover:text-primary-700 underline focus:ring-primary-500'
  };

  const sizeClasses = {
    xs: 'px-2.5 py-1.5 text-xs',
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg'
  };

  const iconSizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6'
  };

  const buttonClasses = `
    ${baseClasses}
    ${variantClasses[variant]}
    ${sizeClasses[size]}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `.trim();

  const iconSize = iconSizeClasses[size];

  const renderIcon = () => {
    if (loading) {
      return <Loader2 className={`${iconSize} animate-spin`} />;
    }
    
    if (icon) {
      const IconComponent = icon;
      return <IconComponent className={iconSize} />;
    }
    
    return null;
  };

  const renderContent = () => {
    const iconElement = renderIcon();
    
    if (!iconElement) return children;
    
    const iconGap = size === 'xs' ? 'ml-1.5' : 'ml-2';
    const iconGapRight = size === 'xs' ? 'mr-1.5' : 'mr-2';

    if (iconPosition === 'left') {
      return (
        <>
          {iconElement}
          {children && <span className={children ? iconGap : ''}>{children}</span>}
        </>
      );
    } else {
      return (
        <>
          {children && <span className={children ? iconGapRight : ''}>{children}</span>}
          {iconElement}
        </>
      );
    }
  };

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={buttonClasses}
      {...props}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
